import { useSelector } from 'react-redux';

import DescriptionList from '@anwb/poncho/components/description-list';
import { List, ListItem } from '@anwb/poncho/components/list';
import Panel from '@anwb/poncho/components/panel';
import { PanelEditHeader } from 'components/RequestFlow/components/PanelEditHeader/PanelEditHeader';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { plCustomerSelector, remarksSelector } from 'store/selectors';
import { PLCustomerData } from 'types/plCustomerData';
import { convertGenderToSalutation } from 'utils/convertGenderToSalutation';
import { STEPS, getStepPathWithCarParam } from 'utils/navigation';

import { customOverviewChangeGA4TrackEvent } from '../util';

export const UserInformation = () => {
	const comment: string = useSelector(remarksSelector);
	const customer: PLCustomerData = useSelector(plCustomerSelector);
	const gaFunnelInfo = useGaFunnelInfo();

	const getFullName = (userInfo: PLCustomerData) =>
		`${userInfo.initials} ${userInfo.lastNamePrefix ? `${userInfo.lastNamePrefix} ` : ''}${
			userInfo.lastName
		}`;

	const getFullAddress = (personalInfo: PLCustomerData) =>
		`${personalInfo.street} ${personalInfo.houseNumber}${personalInfo.houseNumberSuffix || ''}`;

	const getBirthDate = () => {
		if (customer.extraInformation && customer.extraInformation.birthdate) {
			return customer.extraInformation.birthdate;
		}

		if (customer.birthdate) {
			const dateObject = new Date(customer.birthdate);
			if (dateObject.toString() !== 'Invalid Date') {
				return format(dateObject, 'P', { locale: nl });
			}
		}

		return '';
	};

	const personalInformationItems = [
		{
			key: 'Aanhef',
			value: convertGenderToSalutation(customer.gender),
		},
		{
			key: 'Naam',
			value: getFullName(customer),
		},
		{
			key: 'Geboortedatum',
			value: getBirthDate(),
		},
		{
			key: 'Adres',
			value: (
				<List>
					<ListItem>{getFullAddress(customer)}</ListItem>
					<ListItem>{`${customer.postalCode} ${customer.city}`}</ListItem>
				</List>
			),
		},
		{
			key: 'Telefoonnummer',
			value: customer.extraInformation?.phoneNumber || customer.phoneNumber,
		},
		{
			key: 'E-mailadres',
			value: customer.extraInformation?.email || customer.email,
		},
		...(comment
			? [
					{
						key: 'Opmerkingen',
						value: comment,
					},
				]
			: []),
	];

	const title = 'Persoonlijke gegevens';

	const handleClick = () => {
		customOverviewChangeGA4TrackEvent(title, gaFunnelInfo);
	};

	return (
		<Panel
			id="user-information-overview"
			hasOuterSpacing={false}
			dataTest="user-information-overview-panel"
		>
			<Panel.Content>
				<PanelEditHeader
					title="Persoonlijke gegevens"
					onClick={handleClick}
					getStepPath={getStepPathWithCarParam}
					stepToEdit={STEPS.USER_INFORMATION}
					dataTest="user-information-overview-change-link"
				/>

				<DescriptionList items={personalInformationItems} dataTest="userInformation" />
			</Panel.Content>
		</Panel>
	);
};
